<template>
    <aside class="dashboard_left_sidebar_main_wrp">
        <div class="dashboard_left_sidebar">
            <nav class="sidebarin">
                <div class="sidebar">
                    <ul class="nav">
                        <li class="nav-item">
                            <router-link class="nav-link" :to="{ name: 'AiDashboard' }">
                                <span class="sidebar_icon">
                                    <SidebarIcon name="ai" />
                                </span>
                                <span class="sidebar_text">AI</span>
                            </router-link>
                        </li>

                        <li class="nav-item">
                            <router-link class="nav-link" to="/data/">
                                <span class="sidebar_icon">
                                    <SidebarIcon name="data" />
                                </span>
                                <span class="sidebar_text">Data</span>
                            </router-link>
                        </li>

                        <li class="nav-item">
                            <router-link class="nav-link" to="/workspace/">
                                <span class="sidebar_icon">
                                    <span class="badge_wrp">
                                        <i v-if="NotificationMsg > 0" class="badge badge-dark">
                                            {{ NotificationMsg }}
                                        </i>
                                        <i v-if="connectionRequest > 0" class="badge badge-dark connectionRequest">
                                            {{ connectionRequest }}
                                        </i>
                                        <SidebarIcon name="workspace" />
                                    </span>
                                </span>
                                <span class="sidebar_text">Workspace</span>
                            </router-link>
                        </li>

                        <li class="nav-item">
                            <router-link class="nav-link" to="/meeting/">
                                <span class="sidebar_icon">
                                    <SidebarIcon name="meetings" />
                                </span>
                                <span class="sidebar_text">Meetings</span>
                            </router-link>
                        </li>
                    </ul>
                    <div class="extra_space_cover"></div>
                </div>
            </nav>
        </div>
    </aside>
</template>

<script>
import Push from 'push.js';
import audioPlay from '@/assets/audio/piano.mp3';
import { bus } from '@/main';
import SidebarIcon from './SidebarIcon.vue'; // Update this path based on your folder structure

export default {
    name: 'DashboardLeftSidebar',

    components: {
        SidebarIcon
    },

    data() {
        return {
            NotificationMsg: 0,
            connectionRequest: 0,
            supportNotificationMsg: '',

            originalTitle: document.title,
            isTabActive: true,
            titleInterval: null,
            blinkUnreadCount: 0
        };
    },

    watch: {
        // Automatically manages the running loop based on state changes
        blinkUnreadCount(newCount) {
            if (newCount > 0 && !this.isTabActive) {
                this.startTitleBlink();
            } else {
                this.stopTitleBlink();
            }
        }
    },

    mounted() {
        this.initListeners();
    },

    beforeUnmount() {
        this.cleanupListeners();
    },

    methods: {
        initListeners() {
            // Tab Visibility Event Setup
            this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
            document.addEventListener("visibilitychange", this.handleVisibilityChange);

            // Global Bus Bindings
            bus.$on('selfUserUpdateStatus', this.onUserStatusUpdate);
            bus.$on('updateMessageNotifications', this.onMessageNotification);
            bus.$on('userFriendRequestClear', this.onFriendRequestClear);
            bus.$on('clearMessageNotifications', this.onClearMessageNotifications);
        },

        cleanupListeners() {
            document.removeEventListener("visibilitychange", this.handleVisibilityChange);

            bus.$off('selfUserUpdateStatus', this.onUserStatusUpdate);
            bus.$off('updateMessageNotifications', this.onMessageNotification);
            bus.$off('userFriendRequestClear', this.onFriendRequestClear);
            bus.$off('clearMessageNotifications', this.onClearMessageNotifications);

            this.stopTitleBlink();
        },

        /* --- Global Broadcast Receivers --- */

        onUserStatusUpdate(data) {
            if (!window.location.pathname.includes('workspace')) {
                this.NotificationMsg = data.totalUnreadCount || 0;
                this.connectionRequest = data.connection_request_count || 0;
            }
        },

        onMessageNotification(data) {
            const isInWorkspace = window.location.pathname.includes('workspace');

            if (!isInWorkspace) {
                if (data.totalUnreadCount > 0) {
                    if (Push.Permission.has()) {
                        this.showPushNotification(data);
                    } else {
                        this.playNotificationSound(audioPlay);
                    }
                }
                this.NotificationMsg = data.totalUnreadCount;
            }

            if (!this.isTabActive) {
                this.blinkUnreadCount++;
            }
        },

        onFriendRequestClear() {
            this.connectionRequest = 0;
        },

        onClearMessageNotifications() {
            this.NotificationMsg = 0;
            this.resetTitleState();
        },

        handleVisibilityChange() {
            this.isTabActive = !document.hidden;
            if (this.isTabActive) {
                this.resetTitleState();
            }
        },

        /* --- Functional Core Core Utilities --- */

        stripHtml(text) {
            if (!text) return '';
            return text.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
        },

        showPushNotification(data) {
            if (Push.Permission.get() !== 'granted') {
                Push.Permission.request();
                return;
            }

            const cleanMessage = data.message
                ? this.stripHtml(data.message)
                : 'You received a new message';

            Push.create(data.senderName || 'New Notification', {
                body: cleanMessage,
                icon: `${window.location.origin}/favicon.ico`,
                timeout: 3000,
                silent: false,
                onClick: () => {
                    window.focus();
                    setTimeout(() => {
                        const targetRoute = '/workspace/';
                        if (this.$route.path !== targetRoute) {
                            this.$router.push(targetRoute).catch(() => { });
                        }
                    }, 100);
                    // Standard explicit cleanup scoping for notification closure
                    Notification.close();
                }
            });
        },

        resetTitleState() {
            this.blinkUnreadCount = 0;
            document.title = this.originalTitle;
        },

        startTitleBlink() {
            if (this.titleInterval) return;

            this.titleInterval = setInterval(() => {
                document.title = document.title === this.originalTitle
                    ? `(${this.blinkUnreadCount}) New Message`
                    : this.originalTitle;
            }, 1000);
        },

        stopTitleBlink() {
            if (this.titleInterval) {
                clearInterval(this.titleInterval);
                this.titleInterval = null;
            }
        },

        playNotificationSound(sound) {
            if (sound) {
                const audio = new Audio(sound);
                // Catch statement handles auto-play blocks seamlessly without breaking client scripts
                audio.play().catch(err => console.debug("Audio playback contextual delay:", err));
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.dashboard_left_sidebar {
    position: fixed;
    top: 70px;
    height: calc(100% - 80px);
    overflow: auto;
    -webkit-transition: all .7s cubic-bezier(.42, 1.14, 1, 1);
    transition: all .7s cubic-bezier(.42, 1.14, 1, 1);
    width: 110px;
    z-index: 100;
    border-radius: 0 5px 5px 0;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;
    left: 0;
    box-shadow: 5px 5px 10px rgba(138, 145, 153, 0.06);
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: #333333;
    }

    .nav-item {
        position: relative;
        background: rgba(0, 0, 0, .7);
        margin-bottom: 15px;
        width: 100%;
        box-shadow: 3px 3px 3px rgba(255, 255, 255, 0.03);

        .nav-link {
            padding: 20px 0px;
            position: relative;
            display: block;
            color: #d6d6d6;
            text-align: center;
            line-height: 1.3;
            font-size: 16px;
            
            .sidebar_icon {
                display: block;
                color: #ffc107;
            }

            .sidebar_text {
                display: block;
                font-size: 16px;
                margin-top: 10px;
                line-height: 1;
            }


            &:hover {
                background: #87cefa;
                color: #000000;
                .sidebar_icon{color: #000000;}
            }

            &.router-link-active {
                background: #87cefa;
                color: #000000;

                &::before {
                    content: "";
                    border-left: 6px solid #000000;
                    border-top: 6px solid transparent;
                    border-right: 6px solid transparent;
                    border-bottom: 6px solid transparent;
                    position: absolute;
                    left: 0;
                    top: 50%;
                    bottom: auto;
                    transform: translateY(-50%);
                    -webkit-transform: translateY(-50%);
                    -moz-transform: translateY(-50%);
                }
                .sidebar_icon{color: #000000;}
            }

        }

        @media only screen and (max-width:1299px) {
            &:last-child {
                margin-bottom: 0px;
            }
        }
    }

    .extra_space_cover {
        background: rgba(0, 0, 0, 0.7);
        height: calc(100dvh - 532px);
        margin-bottom: 0px;
        box-shadow: 3px 3px 3px rgba(255, 255, 255, 0.03);
    }

    .sidebar {
        position: relative;
        height: 100%;
    }

    .sidebarin {
        height: 100%;
        position: relative;
    }
}

.badge_wrp {
    position: relative;
    height: 34px;
    line-height: 34px;
    display: inline-block;

    .badge {
        position: absolute;
        right: -5px;
        top: -7px;
        font-size: 10px;
        font-family: 'MyriadProRegular', 'Lato', sans-serif;
        font-style: normal;
        width: 18px;
        height: 18px;
        line-height: 17px;
        padding: 0;
        background: rebeccapurple;
        color: #fff;
        border-radius: 2px;
        z-index: 100;
        font-style: italic;
        border-radius: 20px;

        &.connectionRequest {
            left: -10px;
            background-color: #28a745;
            font-style: italic;
            border-radius: 20px;
        }
    }
}
</style>